import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Play, TrendingUp, Clock, DollarSign, ArrowUpRight,
  Megaphone, ChevronDown, ChevronUp, AlertTriangle, Bell, Users,
} from "lucide-react";
import { api } from "../../api";
import ANNOUNCEMENTS from "../../data/announcements";

const LATEST_ANN_ID = ANNOUNCEMENTS[0]?.id || "";
const ANN_COLLAPSED_KEY = "fb_dashboard_announcements_collapsed_v1";
const ANN_SEEN_KEY = "fb_dashboard_announcements_seen_id";

export default function Dashboard() {
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [notif, setNotif] = useState({ alerts: [], can_see_allocations: false });

  useEffect(() => {
    (async () => {
      const now = new Date();
      const start = new Date(now);
      start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
      const from = start.toISOString().slice(0, 10);
      const [e, p, c, i, s, n] = await Promise.all([
        api.entries({ date_from: from }),
        api.projects(),
        api.clients(),
        api.invoicesList(),
        api.reportSummary({ date_from: from }),
        api.dashboardNotifications().catch(() => ({ alerts: [], can_see_allocations: false })),
      ]);
      setEntries(e); setProjects(p); setClients(c); setInvoices(i); setSummary(s); setNotif(n);
    })();
  }, []);

  const projById = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  const cliById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const openInvoices = invoices.filter((i) => i.status !== "Paid").reduce((a, i) => a + i.total, 0);

  const cards = [
    { label: "Tracked this week", value: `${(summary?.total_hours ?? 0).toFixed(2)}h`, icon: Clock },
    { label: "Billable", value: `${(summary?.billable_hours ?? 0).toFixed(2)}h`, icon: TrendingUp },
    { label: "Weekly revenue", value: `$${Math.round(summary?.revenue ?? 0).toLocaleString()}`, icon: DollarSign },
    { label: "Open invoices", value: `$${openInvoices.toLocaleString()}`, icon: ArrowUpRight },
  ];

  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <div className="text-sm text-[#6a736f]">This week</div>
          <h1 className="font-display text-4xl mt-1">Good morning.</h1>
        </div>
        <Link to="/app/time" className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium">
          <Play size={16} /> Start timer
        </Link>
      </div>

      <NotificationsPanel notif={notif} />
      <AnnouncementsPanel />

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-xl border border-cream p-5">
            <div className="flex items-center justify-between">
              <div className="text-xs text-[#6a736f]">{c.label}</div>
              <c.icon size={16} className="text-coral" />
            </div>
            <div className="mt-2 font-display text-3xl">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-white rounded-xl border border-cream p-5">
          <div className="flex items-center justify-between">
            <div className="font-semibold">Recent time entries</div>
            <Link to="/app/time" className="text-xs text-coral">Open timesheet</Link>
          </div>
          {entries.length === 0 ? (
            <p className="text-sm text-[#6a736f] mt-4">No entries yet this week. <Link to="/app/time" className="text-coral">Track your first hour</Link>.</p>
          ) : (
            <div className="mt-4 divide-y divide-[#EAF3EE]" data-testid="recent-entries">
              {entries.slice(0, 10).map((e) => {
                const p = projById[e.project_id];
                const c = p ? cliById[p.client_id] : null;
                return (
                  <div key={e.id} className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: c?.color || "#ccc" }} />
                      <div>
                        <div className="text-sm font-medium">
                          {p ? (
                            <Link to={`/app/projects/${p.id}`}
                                  data-testid={`entry-project-link-${e.id}`}
                                  className="hover:text-coral">
                              {p.name}
                            </Link>
                          ) : "—"}
                          {" — "}
                          <span className="text-[#6a736f]">{e.task}</span>
                        </div>
                        <div className="text-xs text-[#6a736f]">{c?.name || "—"} · {e.notes || "—"}</div>
                      </div>
                    </div>
                    <div className="text-sm tabular-nums">{e.hours.toFixed(2)}h</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-cream p-5">
          <div className="font-semibold">Project budgets</div>
          {projects.length === 0 ? (
            <p className="text-sm text-[#6a736f] mt-4">No projects yet. <Link to="/app/projects" className="text-coral">Create your first project</Link>.</p>
          ) : (
            <div className="mt-4 space-y-4" data-testid="project-budgets">
              {projects.filter((p) => !p.is_archived).slice(0, 5).map((p) => {
                const pct = Math.min(100, Math.round((p.spent_hours / (p.budget_hours || 1)) * 100));
                const c = cliById[p.client_id];
                return (
                  <div key={p.id}>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: c?.color }} />
                        <Link to={`/app/projects/${p.id}`}
                              data-testid={`budget-project-link-${p.id}`}
                              className="truncate hover:text-coral">
                          {p.name}
                        </Link>
                      </div>
                      <span className="text-[#6a736f] text-xs shrink-0">{p.spent_hours}/{p.budget_hours}h</span>
                    </div>
                    <div className="h-2 mt-1.5 bg-[#EAF3EE] rounded-full overflow-hidden">
                      <div className="h-full" style={{ width: `${pct}%`, background: pct > 85 ? "#F97316" : "#22C55E" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------- Notifications
function NotificationsPanel({ notif }) {
  const alerts = notif?.alerts || [];
  if (alerts.length === 0) return null;

  const critical = alerts.filter((a) => a.severity === "critical").length;
  const warning = alerts.filter((a) => a.severity === "warning").length;
  const info = alerts.filter((a) => a.severity === "info").length;

  return (
    <div className="mt-6 bg-white border border-cream rounded-xl overflow-hidden"
         data-testid="notifications-panel">
      <div className="px-5 py-3 border-b border-cream flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex items-center gap-2">
          <div className="p-1.5 rounded-md bg-red-50 text-red-600">
            <Bell size={14} />
          </div>
          <div>
            <div className="font-semibold">Notifications</div>
            <div className="text-xs text-[#6a736f]">
              {critical > 0 && <span className="text-red-600 font-semibold mr-2">{critical} critical</span>}
              {warning > 0 && <span className="text-amber-600 font-semibold mr-2">{warning} warning</span>}
              {info > 0 && <span className="text-[#6a736f]">{info} info</span>}
            </div>
          </div>
        </div>
      </div>
      <div className="divide-y divide-[#EAF3EE] max-h-[280px] overflow-y-auto">
        {alerts.slice(0, 20).map((a) => (
          <NotificationRow key={a.id} alert={a} />
        ))}
      </div>
    </div>
  );
}

function NotificationRow({ alert }) {
  const styles = {
    critical: {
      bg: "bg-red-50/60",
      border: "border-l-red-500",
      icon: <AlertTriangle size={14} className="text-red-600" />,
      text: "text-red-700",
    },
    warning: {
      bg: "bg-amber-50/50",
      border: "border-l-amber-500",
      icon: <AlertTriangle size={14} className="text-amber-600" />,
      text: "text-amber-700",
    },
    info: {
      bg: "bg-blue-50/40",
      border: "border-l-blue-400",
      icon: <Users size={14} className="text-blue-600" />,
      text: "text-blue-700",
    },
  }[alert.severity] || {
    bg: "", border: "border-l-transparent",
    icon: <Bell size={14} />, text: "",
  };
  return (
    <div className={`px-5 py-3 flex items-start gap-3 border-l-4 ${styles.border} ${styles.bg}`}
         data-testid={`notification-${alert.id}`}>
      <div className="mt-0.5">{styles.icon}</div>
      <div className="min-w-0 flex-1">
        <div className={`text-sm font-medium ${styles.text}`}>{alert.title}</div>
        <div className="text-xs text-[#3f4a44] mt-0.5">{alert.detail}</div>
      </div>
      {alert.project_id && (
        <Link to={`/app/projects/${alert.project_id}`}
              data-testid={`notification-link-${alert.id}`}
              className="text-xs text-coral hover:underline shrink-0">
          Open project
        </Link>
      )}
      {alert.user_id && !alert.project_id && (
        <Link to="/app/planning"
              className="text-xs text-coral hover:underline shrink-0">
          Open planning
        </Link>
      )}
    </div>
  );
}

// -------------------------------------------------------------- Announcements
function AnnouncementsPanel() {
  const stored = typeof window !== "undefined" ? localStorage.getItem(ANN_COLLAPSED_KEY) : null;
  const [collapsed, setCollapsed] = useState(stored === "1");
  const [dot, setDot] = useState(false);

  // Show an unread dot until the user opens the panel with the latest ID
  useEffect(() => {
    if (!LATEST_ANN_ID) return;
    const seen = localStorage.getItem(ANN_SEEN_KEY);
    setDot(seen !== LATEST_ANN_ID);
  }, []);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(ANN_COLLAPSED_KEY, next ? "1" : "0");
    if (!next && LATEST_ANN_ID) {
      localStorage.setItem(ANN_SEEN_KEY, LATEST_ANN_ID);
      setDot(false);
    }
  };

  if (!ANNOUNCEMENTS.length) return null;

  return (
    <div className="mt-4 bg-white border border-cream rounded-xl overflow-hidden"
         data-testid="announcements-panel">
      <button onClick={toggle}
              data-testid="announcements-toggle"
              className="w-full px-5 py-3 flex items-center justify-between hover:bg-cream-2/40">
        <div className="inline-flex items-center gap-2">
          <div className="p-1.5 rounded-md bg-coral/10 text-coral relative">
            <Megaphone size={14} />
            {dot && <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-coral" />}
          </div>
          <div className="text-left">
            <div className="font-semibold">What's new in Full Bench</div>
            <div className="text-xs text-[#6a736f]">
              {collapsed ? `${ANNOUNCEMENTS.length} update${ANNOUNCEMENTS.length === 1 ? "" : "s"} — click to expand` : "Latest product updates"}
            </div>
          </div>
        </div>
        {collapsed ? <ChevronDown size={16} className="text-[#6a736f]" /> : <ChevronUp size={16} className="text-[#6a736f]" />}
      </button>
      {!collapsed && (
        <ul className="divide-y divide-[#EAF3EE]">
          {ANNOUNCEMENTS.map((a) => (
            <li key={a.id} className="px-5 py-3" data-testid={`announcement-${a.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-coral/10 text-coral">
                  {a.tag || "Update"}
                </span>
                <span className="text-xs text-[#6a736f]">{a.date}</span>
                <span className="font-medium">{a.title}</span>
              </div>
              <p className="text-sm text-[#3f4a44] mt-1">{a.body}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
